import { useState, useEffect, useRef, useMemo } from "react";
import { Menu, X } from "lucide-react";
import { useSettings } from "../contexts/SettingsContext";

const Header = () => {
  const { sectionSettings } = useSettings();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isHeaderHidden, setIsHeaderHidden] = useState(false);
  const [activeSection, setActiveSection] = useState("home");

  const lastScrollY = useRef(0);

  const allNavItems = [
    { id: "home", label: "Beranda" },
    { id: "about", label: "Tentang", setting: "about" },
    { id: "services", label: "Layanan", setting: "services" },
    { id: "gallery", label: "Galeri", setting: "gallery" },
    { id: "certificates", label: "Sertifikat", setting: "certificates" },
    { id: "testimonials", label: "Testimoni", setting: "testimonials" },
    { id: "contact", label: "Kontak", setting: "contact" },
  ];

  const navItems = useMemo(() => {
    return allNavItems.filter((item) => {
      // home always shows
      if (item.id === "home") return true;
      // other items check their section setting
      return item.setting && sectionSettings[item.setting] !== false;
    });
  }, [sectionSettings]);

  // Scroll handler for direction tracking + scrolled state
  useEffect(() => {
    const handleScroll = () => {
      const currentY = window.scrollY;
      const nextScrolled = currentY > 50;

      setIsScrolled((prev) => (prev === nextScrolled ? prev : nextScrolled));

      // Hide/show on scroll direction
      if (currentY > 100) {
        if (currentY > lastScrollY.current) {
          setIsHeaderHidden(true);
        } else {
          setIsHeaderHidden(false);
        }
      } else {
        setIsHeaderHidden(false);
      }

      lastScrollY.current = currentY;
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // IntersectionObserver for active section tracking
  useEffect(() => {
    const sectionIds = navItems.map((item) => item.id);
    const elements = [];

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      {
        rootMargin: "-40% 0px -55% 0px",
        threshold: 0,
      },
    );

    sectionIds.forEach((id) => {
      const el = document.getElementById(id);
      if (el) {
        observer.observe(el);
        elements.push(el);
      }
    });

    return () => {
      elements.forEach((el) => observer.unobserve(el));
      observer.disconnect();
    };
  }, [navItems]);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
      setIsMobileMenuOpen(false);
    }
  };

  const handleWhatsAppClick = () => {
    const phoneNumber = "6289526775445";
    const message = encodeURIComponent("Halo teh, aku mau booking henna untuk tanggal ....");
    window.open(`https://wa.me/${phoneNumber}?text=${message}`, "_blank");
    setIsMobileMenuOpen(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isMobileMenuOpen
          ? "bg-black/70 backdrop-blur-xl"
          : isScrolled
            ? "bg-black/70 backdrop-blur-xl shadow-lg"
            : "bg-transparent"
      } ${isHeaderHidden ? "-translate-y-full" : "translate-y-0"} ${
        isScrolled && !isMobileMenuOpen
          ? "border-b border-[rgba(212,175,122,0.1)]"
          : ""
      }`}
    >
      <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-auto md:h-20">
          {/* Logo */}
          <div
            className="flex items-center cursor-pointer group"
            onClick={() => scrollToSection("home")}
          >
            <div className="relative">
              {isScrolled && (
                <div className="absolute inset-0 rounded-full bg-[#D4AF7A]/20 blur-xl animate-gold-pulse" />
              )}
              <img
                src="./logo512.png"
                alt="Putri Henna Artist"
                className="relative w-auto h-16 transition-all duration-300 group-hover:scale-110 group-hover:drop-shadow-[0_0_12px_rgba(212,175,122,0.6)]"
              />
            </div>
            <div className="hidden ml-3 sm:block">
              <h1 className="text-lg md:text-xl font-bold text-[#D4AF7A] tracking-wide">
                Teh Putri
              </h1>
              <p className="text-[10px] md:text-xs text-[#F7F5F2] tracking-wider">
                HENNA ARTIST
              </p>
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden space-x-1 md:flex">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className={`px-4 py-2 text-sm font-medium transition-all duration-300 relative group ${
                  activeSection === item.id
                    ? "text-[#D4AF7A]"
                    : "text-[#F7F5F2] hover:text-[#D4AF7A]"
                }`}
              >
                {item.label}
                <span
                  className={`absolute bottom-0 left-0 h-0.5 bg-[#D4AF7A] transition-all duration-300 ${
                    activeSection === item.id
                      ? "w-full"
                      : "w-0 group-hover:w-full"
                  }`}
                />
              </button>
            ))}
          </nav>

          {/* CTA Button */}
          <button
            onClick={handleWhatsAppClick}
            className="hidden md:block text-sm px-6 py-2.5 bg-gradient-to-r from-[#C9A882] to-[#D4AF7A] text-black font-semibold rounded-full hover:shadow-lg hover:shadow-[#D4AF7A]/50 transition-all duration-300 hover:scale-105"
          >
            Booking Sekarang
          </button>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2 text-[#F7F5F2] hover:text-[#D4AF7A] transition-colors"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`md:hidden overflow-hidden transition-all duration-400 ease-out-expo ${
          isMobileMenuOpen ? "max-h-[600px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="px-4 py-6 space-y-3 backdrop-blur-xl bg-black/70">
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => scrollToSection(item.id)}
              className={`block w-full text-left px-4 py-3 rounded-lg transition-all duration-300 ${
                activeSection === item.id
                  ? "text-[#D4AF7A] bg-white/5"
                  : "text-[#F7F5F2] hover:text-[#D4AF7A] hover:bg-white/5"
              }`}
            >
              {item.label}
            </button>
          ))}
          <button
            onClick={handleWhatsAppClick}
            className="block w-full text-center px-6 py-3 bg-gradient-to-r from-[#C9A882] to-[#D4AF7A] text-black font-semibold rounded-full hover:shadow-lg transition-all duration-300"
          >
            Booking Sekarang
          </button>
        </div>
      </div>
    </header>
  );
};

export default Header;
